import BreadCrumb from "Components/Common/BreadCrumb";
import { extractFieldErrors } from "common/lib/fieldErrors";
import {
  addGovtRequest,
  getRequestTypes,
} from "common/services/govt-requests.service";
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import * as Yup from "yup";
import GovernmentRequestForm from "./GovtRequestForm";

const AddGovernmentRequest = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<any>([]);
  const navigate = useNavigate();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      requestTitle: "",
      requestType: "Other",
      projectId: "",
      requestDescription: "",
      isStaffRequest: false,
      noOfPositionsRequested: "",
      positionTitle: "",
      positionType: {
        value: "",
        label: "",
      },
      requestedSalary: null,
      totalValueOfRequest: null,
      periodInMonths: "",
      dateOfRequest: "",
      startDate: "",
      endDate: "",

      contactPerson: "",
      documentAttachments: [],
      fundingStatus: "",
      reviewOfRequest: "",
      agency: {
        value: "",
        label: "",
      },
      region: {
        value: "",
        label: "",
      },
      mda: {
        value: "",
        label: "",
      },
      sector: {
        value: "",
        label: "",
      },
      entity: {
        value: "",
        label: "",
      },
      nameOfRequester: "",
      objectiveOfRequest: "",
      formOfGovernmentRequest: {
        value: "",
        label: "",
      },
      expectedOutputs: "",
      additionalDetails: "",

      selectedFiles: [],
    },
    validationSchema: Yup.object({
      requestTitle: Yup.string().when("requestType", {
        is: (val: string) => val === "Other",
        then: (schema) => schema.required("Please enter request title"),
        otherwise: (schema) => schema.notRequired(),
      }),
      requestType: Yup.string(),
      projectId: Yup.string(),
      noOfPositionsRequested: Yup.number().when("isStaffRequest", {
        is: true,
        then: (schema) => schema.required("Please enter number of positions"),
      }),
      positionTitle: Yup.string().when("isStaffRequest", {
        is: true,
        then: (schema) => schema.required("Please enter position title"),
      }),
      positionType: Yup.object().when("isStaffRequest", {
        is: true,
        then: (schema) =>
          schema.shape({
            value: Yup.string().required("Please select position type"),
          }),
      }),
      requestedSalary: Yup.number().when(["isStaffRequest", "requestType"], {
        is: (isStaffRequest: boolean, requestType: string) =>
          isStaffRequest === true && requestType === "Other",
        then: (schema) => schema.required("Please enter requested salary"),
        otherwise: (schema) => schema.notRequired(),
      }),
      totalValueOfRequest: Yup.number().required("Please enter total value"),
      periodInMonths: Yup.number().when("isStaffRequest", {
        is: (val: string) => val === "Other",
        then: (schema) => schema.required("Please enter period in months"),
        otherwise: (schema) => schema.notRequired(),
      }),
      startDate: Yup.date().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please select start date"),
        otherwise: (schema) => schema.notRequired(),
      }),

      endDate: Yup.date().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please select end date"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dateOfRequest: Yup.date().required("Please select date of request"),

      contactPerson: Yup.string(),
      fundingStatus: Yup.string(),
      reviewOfRequest: Yup.string().when(["requestType", "isStaffRequest"], {
        is: (requestType: string, isStaffRequest: boolean) =>
          requestType === "Other" && isStaffRequest === true,
        then: (schema) => schema.required("Please enter review of request"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // entity: Yup.object().shape({
      //   value: Yup.string().required("Please select entity"),
      // }),
      // agency: Yup.object().when("entity.value", {
      //   is: (val: string) => val === "UN" || val === "Donor",
      //   then: (schema) =>
      //     schema.shape({
      //       value: Yup.string().required("Please select agency"),
      //     }),
      //   otherwise: (schema) =>
      //     schema.shape({
      //       value: Yup.string().notRequired(),
      //     }),
      // }),
      agency: Yup.object().shape({
        value: Yup.string().required("Please select agency"),
      }),
      region: Yup.object().shape({
        value: Yup.string().required("Please select region"),
      }),
      mda: Yup.object().shape({
        value: Yup.string().required("Please select MDA"),
      }),

      nameOfRequester: Yup.string().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please enter name of requester"),
        otherwise: (schema) => schema.notRequired(),
      }),
      objectiveOfRequest: Yup.string().when("requestType", {
        is: (val: string) => val !== "Other",
        then: (schema) => schema.required("Please enter objective of request"),
        otherwise: (schema) => schema.notRequired(),
      }),
      formOfGovernmentRequest: Yup.object()
        .shape({
          value: Yup.string(),
        })
        .when("requestType", {
          is: (val: string) => val !== "Other",
          then: (schema) =>
            schema.shape({
              value: Yup.string().required(
                "Please select form of government request"
              ),
            }),
          otherwise: (schema) =>
            schema.shape({
              value: Yup.string().notRequired(),
            }),
        }),
      expectedOutputs: Yup.string(),
      additionalDetails: Yup.string(),
      selectedFiles: Yup.array(),
      // .min(1, "Atleast one file is required")
      // .required("Required"),
    }),
    onSubmit: async (values: any) => {
      try {
        let data = {};
        const requestTypeResponse = await getRequestTypes({
          filters: {
            code: values.requestType,
          },
        });
        const requestTypeId = requestTypeResponse?.data[0];

        const startDate = values.startDate;
        const endDate = values.endDate;

        let monthDifference = null;

        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);

          monthDifference =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());

          // If you want to include partial months based on day difference:
          if (end.getDate() < start.getDate()) {
            monthDifference -= 1;
          }
        }

        if (values.requestType === "Other") {
          data = {
            requestTitle: values?.requestTitle || null,
            projectId: values?.projectId || null,
            typeOfRequest: requestTypeId || null,
            requestDescription: values?.requestDescription || null,
            isStaffRequest: values?.isStaffRequest || null,
            noOfPositionsRequested: `${values?.noOfPositionsRequested}` || null,
            positionTitle: values?.positionTitle || null,
            requestedSalary: values?.requestedSalary || null,
            totalValueOfRequest: values?.totalValueOfRequest || null,
            periodInMonths: values?.periodInMonths?.toString() || null,
            dateOfRequest:
              (values?.dateOfRequest &&
                values?.dateOfRequest?.toLocaleDateString("en-CA")) ||
              null,
            startDate: null,
            endDate: null,
            contactPerson: null,
            fundingStatus: values?.fundingStatus || null,
            reviewOfRequest: values?.reviewOfRequest || null,
            agency: values?.agency?.value || null,
            region: values?.region?.value || null,
            mda: values?.mda?.value || null,
            sector: values?.sector?.value || null,
            entity: values?.entity?.label || null,
            positionType: values?.positionType?.value || null,
            formOfGovernmentRequest: null,
            nameOfRequester: null,
            objectiveOfRequest: null,
            expectedOutputs: null,
            additionalDetails: values?.additionalDetails || null,
          };
        } else {
          data = {
            requestTitle: null,
            projectId: null,
            typeOfRequest: requestTypeId || null,
            requestDescription: null,
            isStaffRequest: null,
            noOfPositionsRequested: null,
            positionTitle: null,
            requestedSalary: null,
            totalValueOfRequest: values?.totalValueOfRequest || null,
            periodInMonths: `${monthDifference}`,
            dateOfRequest:
              (values?.dateOfRequest &&
                values?.dateOfRequest?.toLocaleDateString("en-CA")) ||
              null,
            startDate:
              (values?.startDate &&
                values.startDate?.toLocaleDateString("en-CA")) ||
              null,
            endDate:
              (values?.endDate &&
                values?.endDate?.toLocaleDateString("en-CA")) ||
              null,
            contactPerson: values?.contactPerson || null,
            fundingStatus: values?.fundingStatus || null,
            reviewOfRequest: null,
            agency: values?.agency?.value || null,
            mda: values?.mda?.value || null,
            sector: null,
            entity: values?.entity?.label || null,
            positionType: null,
            formOfGovernmentRequest:
              values?.formOfGovernmentRequest?.value || null,
            nameOfRequester: values?.nameOfRequester || null,
            objectiveOfRequest: values?.objectiveOfRequest || null,
            expectedOutputs: values?.expectedOutputs || null,
            additionalDetails: values?.additionalDetails || null,
          };
        }

        const formData = new FormData();
        values.selectedFiles.forEach((file: any) => {
          formData.append("files.documentAttachments", file, file.name);
        });
        formData.append("data", JSON.stringify(data));

        setLoading(true);
        await addGovtRequest(formData);
        // navigate("/government-requests/" + response?.payload?.data?.id);
        navigate("/government-requests/");
      } catch (error: any) {
        const errors = extractFieldErrors(error?.response);
        setFieldErrors(errors);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Add Government Request"
          pageTitles={[
            {
              title: "Dashboard",
              link: "/",
            },
            { title: "Government Requests", link: "/government-requests" },
          ]}
          back
        />
        <GovernmentRequestForm
          validation={validation}
          isUpdate={false}
          loading={loading}
          fieldErrors={fieldErrors}
        />
      </Container>
    </div>
  );
};

export default AddGovernmentRequest;
